import { useState, useEffect } from 'react'
import { fetchAdoptionStories, INITIAL_ADOPTION_STORIES } from '../../services/api'

const AdoptionStories = () => {
  const [stories, setStories] = useState(INITIAL_ADOPTION_STORIES)
  const [startIndex, setStartIndex] = useState(0)
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
  const isMobile = windowWidth < 768

  // Mouse Drag / Touch Swipe State
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)

  useEffect(() => {
    fetchAdoptionStories().then((data) => {
      if (data && data.length > 0) {
        setStories(data)
      }
    })
  }, [])

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isSm = windowWidth >= 640 && windowWidth < 768
  const isMd = windowWidth >= 768 && windowWidth < 1024
  const isDesktop = windowWidth >= 1024

  const visibleCards = isMobile ? 1 : isSm ? 2 : isMd ? 3 : 4
  const maxStartIndex = Math.max(0, stories.length - visibleCards)

  const handleNext = () => {
    setStartIndex((prev) => (prev >= maxStartIndex ? 0 : prev + 1))
  }

  const handlePrev = () => {
    setStartIndex((prev) => (prev <= 0 ? maxStartIndex : prev - 1))
  }

  // Mouse Drag & Touch Handlers
  const handleDragStart = (clientX) => {
    setIsDragging(true)
    setStartX(clientX)
    setDragOffset(0)
  }

  const handleDragMove = (clientX) => {
    if (!isDragging) return
    const diff = clientX - startX
    setDragOffset(diff)
  }

  const handleDragEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    const threshold = 50 // px threshold to trigger slide shift
    if (dragOffset < -threshold) {
      handleNext()
    } else if (dragOffset > threshold) {
      handlePrev()
    }
    setDragOffset(0)
  }

  return (
    <section className="bg-transparent py-8 sm:py-12 lg:py-16 2xl:py-20 select-none relative">
      <div className="max-w-8xl mx-auto w-full">
        
        {/* Header with Navigation Controls */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-4 sm:mb-6 md:mb-8 gap-3 sm:gap-4 px-4 sm:px-6 lg:px-8 2xl:px-16">
          <div className="text-left">
            <span className="text-[11px] sm:text-xs 2xl:text-sm font-bold tracking-widest text-brand-orange-700 uppercase block mb-1">
              FOREVER HOMES
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl text-slate-900 font-bold tracking-tight">
              Happy Adoption Stories
            </h2>
            <p className="text-slate-600 text-xs sm:text-sm 2xl:text-base font-medium mt-1 sm:mt-1.5 max-w-xl">
              Read how rescued animals found loving forever homes across Bengaluru.
            </p>
          </div>

          {/* Sliding Arrow Navigation Controls */}
          {stories.length > visibleCards && (
            <div className="hidden md:flex items-center gap-2 self-start sm:self-auto">
              <button
                onClick={handlePrev}
                className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center bg-white text-slate-700 hover:bg-brand-orange-700 hover:text-white transition-all shadow-xs focus:outline-none cursor-pointer border border-slate-200/80"
                aria-label="Previous adoption story"
              >
                <svg className="w-4 h-4 2xl:w-5 2xl:h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>
              <button
                onClick={handleNext}
                className="w-11 h-11 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center bg-white text-slate-700 hover:bg-brand-orange-700 hover:text-white transition-all shadow-xs focus:outline-none cursor-pointer border border-slate-200/80"
                aria-label="Next adoption story"
              >
                <svg className="w-4 h-4 2xl:w-5 2xl:h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Sliding & Drag-enabled Carousel track */}
        <div 
          className="w-full overflow-hidden py-1 sm:py-2 px-4 sm:px-6 lg:px-8 2xl:px-16 cursor-grab active:cursor-grabbing touch-pan-y select-none"
          onMouseDown={(e) => handleDragStart(e.clientX)}
          onMouseMove={(e) => handleDragMove(e.clientX)}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
          onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
          onTouchEnd={handleDragEnd}
        >
          <div
            className={`flex gap-3.5 sm:gap-5 lg:gap-5 ${isDragging ? 'transition-none' : 'transition-transform duration-500 ease-in-out'}`}
            style={{
              transform: `translateX(calc(-${startIndex} * (${
                isMobile ? '84%' : isSm ? '50%' : isMd ? '33.333%' : '25%'
              } + ${isMobile ? '0.875rem' : '1.25rem'}) + ${dragOffset}px))`
            }}
          >
            {stories.map((story) => (
              <div
                key={story.id}
                className="w-[84%] sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-0.85rem)] lg:w-[calc(25%-0.95rem)] 2xl:w-[calc(25%-1rem)] shrink-0 bg-white rounded-tl-[32px] rounded-br-[32px] sm:rounded-tl-[44px] sm:rounded-br-[44px] 2xl:rounded-tl-[52px] 2xl:rounded-br-[52px] rounded-tr-none rounded-bl-none p-3.5 sm:p-4 lg:p-4.5 transition-all duration-300 flex flex-col justify-between hover:-translate-y-1 group text-left pointer-events-none shadow-xs hover:shadow-md border border-slate-100/90"
              >
                {/* Pet Photo - Curved image frame */}
                <div className="relative h-38 xs:h-42 sm:h-46 md:h-48 lg:h-48 2xl:h-54 w-full rounded-tl-[24px] rounded-br-[24px] sm:rounded-tl-[32px] sm:rounded-br-[32px] 2xl:rounded-tl-[40px] 2xl:rounded-br-[40px] rounded-tr-none rounded-bl-none overflow-hidden bg-brand-orange-50/50 mb-3 shrink-0">
                  <img
                    src={story.image_url || story.image || '/adopters/1.png'}
                    alt={story.pet_name || 'Adopted animal story'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/adopters/1.png'; }}
                  />
                  {story.date && (
                    <span className="absolute bottom-2 left-2 sm:bottom-2.5 sm:left-2.5 px-2 py-0.5 sm:px-2.5 sm:py-0.5 bg-white/95 text-slate-800 text-[9px] xs:text-[10px] font-extrabold uppercase tracking-widest rounded-md shadow-xs border border-white/60">
                      {story.date}
                    </span>
                  )}
                </div>

                {/* Card Description & Meta */}
                <div className="flex flex-col flex-grow justify-between px-0.5 space-y-2">
                  <p className="text-slate-700 text-xs sm:text-xs md:text-sm font-semibold leading-snug sm:leading-relaxed">
                    "{story.quote}"
                  </p>
                  
                  {(story.pet_name || story.adopter_name) && (
                    <div className="pt-1.5 border-t border-slate-100">
                      <h3 className="font-bold text-slate-900 text-[11px] sm:text-xs md:text-sm">
                        {story.pet_name && <span className="text-brand-orange-700">{story.pet_name}</span>}
                        {story.pet_name && story.adopter_name && <span className="text-slate-400 font-normal"> with </span>}
                        {story.adopter_name && <span className="text-slate-700">{story.adopter_name}</span>}
                      </h3>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile controls row: wavy goggles shape with narrowed center bridge and larger chevrons */}
        {stories.length > visibleCards && (
          <div className="flex md:hidden justify-end items-center mt-3 sm:mt-4 px-4 sm:px-6">
            <div className="relative inline-flex items-center justify-between w-[96px] h-10 select-none">
              {/* Organic Wavy Goggles Background (Pinched Waist Center) */}
              <svg
                viewBox="0 0 100 44"
                className="absolute inset-0 w-full h-full text-brand-orange-500 fill-current pointer-events-none"
                preserveAspectRatio="none"
              >
                <path d="M22 0 C34 0 38 14 50 14 C62 14 66 0 78 0 C90.15 0 100 9.85 100 22 C100 34.15 90.15 44 78 44 C66 44 62 30 50 30 C38 30 34 44 22 44 C9.85 44 0 34.15 0 22 C0 9.85 9.85 0 22 0 Z" />
              </svg>

              {/* Left Chevron Button */}
              <button
                onClick={handlePrev}
                className="relative z-10 w-10 h-10 flex items-center justify-center text-white active:scale-90 transition-transform cursor-pointer"
                aria-label="Previous story"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>

              {/* Right Chevron Button */}
              <button
                onClick={handleNext}
                className="relative z-10 w-10 h-10 flex items-center justify-center text-white active:scale-90 transition-transform cursor-pointer"
                aria-label="Next story"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </div>
          </div>
        )}

      </div>
    </section>
  )
}

export default AdoptionStories
